import moment from 'moment';
import { CheckIcon, EyeIcon } from '@heroicons/react/24/outline';
import { InboxKind, InboxStatus } from '_graphql/__generated__/globalTypes';
import { SupplierInbox_supplierInbox } from '_graphql/queries/__generated__/SupplierInbox';
import { inboxKindLabel } from './deepLink';

type ColumnHandlers = {
  onOpen: (item: SupplierInbox_supplierInbox) => void;
  onAttend?: (item: SupplierInbox_supplierInbox) => void;
  attendingId?: string | null;
  attended?: boolean;
};

const KIND_BADGE: Record<InboxKind, string> = {
  [InboxKind.FAILED_ORDER_CHARGE]: 'bg-red-100 text-red-800',
  [InboxKind.WALLET_PAYOUT_PENDING]: 'bg-amber-100 text-amber-800',
  [InboxKind.ORDER_CREATED]: 'bg-sky-100 text-sky-800',
  [InboxKind.ORDER_STATUS_CHANGED]: 'bg-indigo-100 text-indigo-800',
  [InboxKind.STOCK_UPDATED]: 'bg-gray-100 text-gray-700',
};

const whenColumn = {
  name: 'When',
  accessor: 'createdAt',
  render: (row: SupplierInbox_supplierInbox) => (
    <div className="whitespace-nowrap text-sm text-gray-700">
      <div>{moment(row.createdAt).format('MMM DD, YYYY')}</div>
      <div className="text-xs text-gray-500">
        {moment(row.createdAt).fromNow()}
      </div>
    </div>
  ),
};

const kindColumn = {
  name: 'Kind',
  accessor: 'kind',
  render: (row: SupplierInbox_supplierInbox) => (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${
        KIND_BADGE[row.kind] ?? 'bg-gray-100 text-gray-700'
      }`}
    >
      {inboxKindLabel[row.kind] ?? row.kind}
    </span>
  ),
};

const itemColumn = {
  name: 'Item',
  accessor: 'title',
  render: (row: SupplierInbox_supplierInbox) => (
    <div className="max-w-md">
      <div className="line-clamp-1 text-sm font-medium text-gray-900">
        {row.title}
      </div>
      <div className="line-clamp-2 text-xs text-gray-500">{row.body}</div>
    </div>
  ),
};

const statusColumn = {
  name: 'Status',
  accessor: 'status',
  render: (row: SupplierInbox_supplierInbox) => (
    <span
      className={
        row.status === InboxStatus.OPEN
          ? 'inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800'
          : 'inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800'
      }
    >
      {row.status === InboxStatus.OPEN ? 'Open' : 'Attended'}
    </span>
  ),
};

const attendedByColumn = {
  name: 'Attended',
  accessor: 'attendedBy',
  render: (row: SupplierInbox_supplierInbox) => (
    <div className="whitespace-nowrap text-sm text-gray-700">
      <div className="line-clamp-1">{row.attendedBy || 'Auto-resolved'}</div>
      {row.attendedAt ? (
        <div className="text-xs text-gray-500">
          {moment(row.attendedAt).fromNow()}
        </div>
      ) : null}
    </div>
  ),
};

export const inboxColumns = ({
  onOpen,
  onAttend,
  attendingId,
  attended,
}: ColumnHandlers) => [
  whenColumn,
  kindColumn,
  itemColumn,
  attended ? attendedByColumn : statusColumn,
  {
    name: 'Action',
    accessor: '',
    render: (row: SupplierInbox_supplierInbox) => (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onOpen(row)}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
        >
          <EyeIcon className="h-4 w-4" />
          Open
        </button>
        {onAttend && row.status === InboxStatus.OPEN ? (
          <button
            type="button"
            disabled={attendingId === row.id}
            onClick={() => onAttend(row)}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <CheckIcon className="h-4 w-4" />
            {attendingId === row.id ? 'Marking…' : 'Mark done'}
          </button>
        ) : null}
      </div>
    ),
  },
];

export const kindFilterOptions = [
  { label: 'All kinds', value: '' },
  ...Object.values(InboxKind).map((value) => ({
    label: inboxKindLabel[value],
    value,
  })),
];
